/**
 * #477 phase 1 — audit-only self-report of an OpenClaw/Hermes tool call to the
 * backend's hash-chained audit trail (POST /api/v1/audit/entries).
 *
 * WHY: the enclave fence and hash-chained audit exist and work, but only for the
 * native claude-room-bridge armed-room path. Verified on prod 2026-07-02: an
 * OpenClaw agent could read local files from a plain 1:1 with no AgentVault
 * audit entry at all — `agent_tools` for that device held zero rows, and the
 * only tool-audit rows in the tenant came from the bridge. The machinery was
 * never reached from this plugin.
 *
 * This is NOT a vulnerability: a 1:1 is E2E-encrypted, so only the owner can
 * drive the agent's tools. It is an observability gap — the platform cannot see
 * what its own agents do.
 *
 * Mirrors claude-room-bridge/src/audit-report.ts deliberately rather than
 * sharing code: the two packages ship independently, and a shared module would
 * couple their release cycles for ~30 lines.
 *
 * UNTRUSTED self-report. This runs on the owner's machine, so it is a record and
 * a signal, never a guarantee — the same standing as the bridge's report.
 *
 * ⚠️ MUST NEVER change the outcome of a tool call. `before_tool_call` returns an
 * approval decision, and its handler swallows exceptions by returning `{}` —
 * which means ALLOW. A throw from here would therefore convert a policy DENY
 * into an allow. Every path below is wrapped; this function cannot throw.
 */
export interface PluginToolAuditReport {
  apiUrl: string;
  deviceJwt: string | null;
  agentId: string | null;
  tool: string;
  /** `allow` — no policy objected. `require_approval` — the enforcer asked for owner confirmation. */
  decision: "allow" | "require_approval";
  reason?: string;
}

export function reportPluginToolAudit(opts: PluginToolAuditReport): void {
  try {
    if (!opts.deviceJwt || !opts.apiUrl || !opts.tool) return;
    const body = {
      // `agent_tool:` rather than the bridge's `room_tool:` so the two lanes stay
      // separable in audit queries — "what did agents do in rooms" and "what did
      // they do in DMs" are different questions.
      action: `agent_tool:${opts.tool}`,
      agent_id: opts.agentId ?? undefined,
      details: {
        tool: opts.tool,
        decision: opts.decision,
        lane: "dm",
        ...(opts.reason ? { reason: opts.reason } : {}),
        source: "openclaw-plugin",
      },
    };
    void fetch(`${opts.apiUrl}/api/v1/audit/entries`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${opts.deviceJwt}` },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(5000),
    }).catch(() => {
      /* fire-and-forget: audit is a record, not a gate */
    });
  } catch {
    /* never let a self-report affect a turn — see the warning above */
  }
}
